import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { serializeJsonLd, type JsonLd } from "../../lib/jsonLd";
import {
  SITE_DEFAULTS,
  absoluteImageUrl,
  absoluteUrl,
  formatPageTitle,
  truncateDescription,
} from "../../lib/siteSeo";

export type PageMetaProps = {
  title: string;
  description: string;
  /** Canonical path; defaults to current location pathname. */
  path?: string;
  image?: string;
  type?: "website" | "article";
  publishedTime?: string;
  keywords?: string;
  noIndex?: boolean;
  /** Extra JSON-LD object(s) for this page. */
  jsonLd?: JsonLd | JsonLd[] | null;
};

export function PageMeta({
  title,
  description,
  path,
  image,
  type = "website",
  publishedTime,
  keywords = SITE_DEFAULTS.keywords,
  noIndex = false,
  jsonLd,
}: PageMetaProps): JSX.Element {
  const { pathname } = useLocation();
  const canonicalPath = path ?? pathname;
  const canonicalUrl = absoluteUrl(canonicalPath);
  const ogImage = absoluteImageUrl(image ?? SITE_DEFAULTS.image);
  const fullTitle = formatPageTitle(title);
  const metaDescription = truncateDescription(description);
  const jsonLdPayload = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : null;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:locale" content={SITE_DEFAULTS.locale} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_DEFAULTS.siteName} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_DEFAULTS.twitterSite} />
      <meta name="twitter:creator" content={SITE_DEFAULTS.twitterSite} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {type === "article" && publishedTime && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:author" content={SITE_DEFAULTS.url} />
          <meta property="article:publisher" content={SITE_DEFAULTS.url} />
        </>
      )}

      {jsonLdPayload?.map((node, index) => (
        <script key={`jsonld-${index}`} type="application/ld+json">
          {serializeJsonLd(node)}
        </script>
      ))}
    </Helmet>
  );
}

/** Convenience wrapper for entries in staticPageMeta.ts */
export function StaticPageMeta({
  meta,
  jsonLd,
}: {
  meta: import("../../lib/staticPageMeta").StaticPageMeta;
  jsonLd?: JsonLd | JsonLd[] | null;
}): JSX.Element {
  return (
    <PageMeta
      title={meta.title}
      description={meta.description}
      path={meta.path}
      image={meta.image}
      noIndex={meta.noIndex}
      jsonLd={jsonLd}
    />
  );
}
